import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import type { Client } from '../types';
import { AdminLayout } from '../components/Layout';

interface AdminLead {
  id: number;
  client_id: string;
  client_name: string;
  owner_firstname: string;
  owner_lastname: string;
  owner_phone: string;
  prop_address: string;
  status: string;
  lead_score: number;
  lead_cost: number;
  is_hidden_from_client: boolean | number;
  created_at: string;
  campaign_name: string;
}

export function AdminLeads() {
  const [admin, setAdmin] = useState<any>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [campaigns, setCampaigns] = useState<{ name: string, client_id: number }[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [leads, setLeads] = useState<AdminLead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Status Change State
  const [editingLead, setEditingLead] = useState<AdminLead | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  // Add Lead State
  const initialLeadForm = {
    client_id: '',
    owner_firstname: '',
    owner_lastname: '',
    owner_phone: '',
    owner_email: '',
    prop_address: '',
    prop_type: 'SFH',
    bedrooms: '',
    bathrooms: '',
    land_size: '',
    land_utilities: '',
    updates_text: '',
    market_val: '',
    ask_price: '',
    prop_license: '0',
    occupancy: '0',
    listing: '0',
    mortgage: '0',
    mortgage_amount: '',
    owshp_tmln: '0',
    sell_reason: '0',
    closn_tmln: '0',
    info: '',
    motivation: '5',
    cond_scale: '5',
    lead_score: '5',
    lead_cost: '0',
    campaign_name: '',
    recording_url: '',
    status: 'new'
  };

  const [showAddLead, setShowAddLead] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState(initialLeadForm);

  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open Add Lead modal from sidebar button
  useEffect(() => {
    if (searchParams.get('add') === 'true') {
      setShowAddLead(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const getApiBase = () => {
    return '/api';
  };

  const request = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${getApiBase()}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const fetchCampaigns = async () => {
    try {
      const data = await request('/admin/campaigns');
      setCampaigns(data);
    } catch { }
  };

  useEffect(() => {
    const stored = localStorage.getItem('rewagent_admin');
    if (stored) {
      setAdmin(JSON.parse(stored));
      fetchClients();
      fetchCampaigns();
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const data = await request('/admin/clients');
      setClients(data);
    } catch { }
  };

  useEffect(() => {
    if (admin) fetchLeads();
  }, [admin, page, selectedClient, statusFilter, search]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (selectedClient) query.append('client_id', selectedClient);
      if (statusFilter) query.append('status', statusFilter);
      if (search) query.append('search', search);

      const data = await request(`/admin/leads/all?${query.toString()}`);
      setLeads(data.leads || []);
      setTotal(data.pagination?.total || 0);
    } catch { }
    finally {
      setLoading(false);
    }
  };

  const handleHideLead = async (lead: AdminLead) => {
    if (!confirm(`Are you sure you want to hide the lead ${lead.owner_firstname} ${lead.owner_lastname} from the client view?`)) return;
    try {
      await request(`/admin/leads/${lead.id}/hide`, { method: 'PUT' });
      fetchLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to hide lead');
    }
  };

  const handleUnhideLead = async (lead: AdminLead) => {
    if (!confirm(`Restore the lead ${lead.owner_firstname} ${lead.owner_lastname} back to the client view?`)) return;
    try {
      await request(`/admin/leads/${lead.id}/unhide`, { method: 'PUT' });
      fetchLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unhide lead');
    }
  };

  const handleDeleteLead = async (lead: AdminLead) => {
    if (!confirm(`WARNING: Are you sure you want to PERMANENTLY delete the lead ${lead.owner_firstname} ${lead.owner_lastname} from the database? This cannot be undone.`)) return;
    try {
      await request(`/admin/leads/${lead.id}`, { method: 'DELETE' });
      fetchLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete lead');
    }
  };

  const handleStatusChange = async () => {
    if (!editingLead) return;
    setActionLoading(true);
    try {
      await request(`/admin/leads/${editingLead.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus, reason: statusReason })
      });
      setEditingLead(null);
      fetchLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading(false);
      setNewStatus('');
      setStatusReason('');
    }
  };

  const handleAddLead = async () => {
    if (!newLeadForm.client_id || !newLeadForm.owner_firstname || !newLeadForm.owner_phone || !newLeadForm.prop_address) {
      alert('Please fill out all required fields: Client, First Name, Phone, and Property Address.');
      return;
    }
    setActionLoading(true);
    try {
      await request('/admin/leads', {
        method: 'POST',
        body: JSON.stringify(newLeadForm)
      });
      setShowAddLead(false);
      setNewLeadForm(initialLeadForm);
      fetchLeads();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add lead');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await request('/auth/logout', { method: 'POST' }); } catch { }
    localStorage.removeItem('rewagent_admin');
    localStorage.removeItem('rewagent_token');
    navigate('/admin');
  };

  if (!admin) return null;

  return (
    <AdminLayout
      activeTab="leads"
      onTabChange={(tab) => {
        const baseTab = tab.split('?')[0];
        navigate(`/admin/${['leads', 'access-codes', 'campaigns', 'logs'].includes(baseTab) ? tab : 'dashboard'}`);
      }}
      onLogout={handleLogout}
    >
      <div className="p-10 max-w-7xl mx-auto">
        <div className="mb-10 flex justify-between items-end">
          <div>
            <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2 tracking-tight">Leads Overview</h1>
            <p className="text-[#6b635a] text-sm uppercase tracking-widest font-semibold">Global system leads</p>
          </div>
          <button onClick={() => setShowAddLead(true)} className="px-5 py-2.5 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg text-sm font-medium transition-all">
            + Manual Lead
          </button>
        </div>

        <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <div>
              <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Filter by Client</label>
              <select value={selectedClient} onChange={(e) => { setSelectedClient(e.target.value); setPage(1); }}
                className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a]">
                <option value="">All Clients</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Filter by Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a]">
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="qualified">Qualified</option>
                <option value="disqualified">Disqualified</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-3 block">Search Context</label>
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, phone, address..."
                className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a]" />
            </div>
          </div>
        </div>

        <div className="mb-5 text-sm font-medium text-[#8c8578] uppercase tracking-widest">
          {total === 0 ? '0 LEADS' : `${leads.length} OF ${total} LEADS`}
        </div>

        <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e3dbcf]">
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Client</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Owner Info</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Score</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Cost</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Status</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e3dbcf]">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-[#8c8578]">Loading leads...</td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-[#8c8578]">No leads found</td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-[#f3ede3]/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-[#1a1a1a]">{lead.client_name}</div>
                        <div className="text-xs text-[#8c8578]">{lead.campaign_name || 'No Campaign'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-[#1a1a1a]">{lead.owner_firstname} {lead.owner_lastname}</div>
                        <div className="text-xs text-[#6b635a]">{lead.owner_phone}</div>
                        <div className="text-xs text-[#8c8578] truncate max-w-xs">{lead.prop_address}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-bold ${lead.lead_score >= 7 ? 'text-green-600' : lead.lead_score >= 4 ? 'text-amber-600' : 'text-red-600'}`}>
                          {lead.lead_score}
                        </span>
                        <span className="text-xs text-[#b8b0a5]">/10</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-[#1a1a1a]">${Number(lead.lead_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded text-xs font-semibold tracking-wide border ${lead.status === 'qualified' ? 'bg-green-100 text-green-800 border-green-200' :
                          lead.status === 'disqualified' ? 'bg-red-100 text-red-800 border-red-200' :
                            'bg-[#f3ede3] text-[#1a1a1a] border-[#e3dbcf]'
                          }`}>
                          {lead.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/leads/${lead.id}`}
                            className="px-3 py-2 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg text-xs font-medium transition-all"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => { setEditingLead(lead); setNewStatus(lead.status); setStatusReason(''); }}
                            className="px-3 py-2 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#1a1a1a] hover:text-[#f3ede3] hover:border-[#1a1a1a] rounded-lg text-xs font-medium transition-all"
                          >
                            Status
                          </button>
                          {lead.is_hidden_from_client ? (
                            <button
                              onClick={() => handleUnhideLead(lead)}
                              title="Click to restore to client view"
                              className="px-3 py-2 bg-[#e3dbcf] text-[#8c8578] border border-[#e3dbcf] hover:bg-[#fcfbfa] hover:text-orange-600 hover:border-orange-300 rounded-lg text-xs font-medium transition-all"
                            >
                              Hidden
                            </button>
                          ) : (
                            <button
                              onClick={() => handleHideLead(lead)}
                              title="Hide from Client"
                              className="px-3 py-2 bg-[#fcfbfa] text-orange-600 border border-[#e3dbcf] hover:bg-orange-600 hover:text-[#fcfbfa] rounded-lg text-xs font-medium transition-all"
                            >
                              Hide
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteLead(lead)}
                            title="Delete Permanently"
                            className="px-3 py-2 bg-[#fcfbfa] text-red-600 border border-[#e3dbcf] hover:bg-red-600 hover:text-[#fcfbfa] rounded-lg text-xs font-medium transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {total > leads.length && <div className="p-4 border-t border-[#e3dbcf] text-center text-sm text-[#8c8578]">Pagination not shown in admin view</div>}
        </div>

        {/* Modal for modifying status */}
        {
          editingLead && (
            <div className="fixed inset-0 bg-[#1a1a1a]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-[#fcfbfa] rounded-2xl p-8 max-w-md w-full shadow-2xl border border-[#e3dbcf]">
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Modify Lead Status</h3>
                <p className="text-sm text-[#6b635a] mb-6">Updating status for {editingLead.owner_firstname} {editingLead.owner_lastname}</p>

                <div className="space-y-4 mb-8">
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">New Status</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:border-[#1a1a1a]"
                    >
                      <option value="new">New</option>
                      <option value="qualified">Qualified</option>
                      <option value="disqualified">Disqualified</option>
                    </select>
                  </div>
                  {newStatus === 'disqualified' && (
                    <div>
                      <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Reason / Note (Required)</label>
                      <textarea
                        value={statusReason}
                        onChange={(e) => setStatusReason(e.target.value)}
                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:border-[#1a1a1a] min-h-[100px]"
                        placeholder="Enter reason for disqualification..."
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleStatusChange}
                    disabled={actionLoading || (newStatus === 'disqualified' && !statusReason.trim())}
                    className="flex-1 py-3 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg font-medium transition-all disabled:opacity-50"
                  >
                    {actionLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditingLead(null)}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#f3ede3] rounded-lg font-medium transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {/* Modal for adding a new lead */}
        {
          showAddLead && (
            <div className="fixed inset-0 bg-[#1a1a1a]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-[#fcfbfa] rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-[#e3dbcf] my-8">
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Add Manual Lead</h3>
                <p className="text-sm text-[#6b635a] mb-8">Enter comprehensive lead details into a client's pipeline</p>

                {/* SECTION: SYSTEM ADMIN */}
                <h4 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4 border-b border-[#e3dbcf] pb-2">System Admin Info</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Client (Required)</label>
                    <select
                      value={newLeadForm.client_id}
                      onChange={(e) => setNewLeadForm({ ...newLeadForm, client_id: e.target.value })}
                      className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]"
                    >
                      <option value="">Select a Client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Campaign Name (Required)</label>
                    <select
                      value={newLeadForm.campaign_name}
                      onChange={(e) => setNewLeadForm({ ...newLeadForm, campaign_name: e.target.value })}
                      className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]"
                      disabled={!newLeadForm.client_id}
                    >
                      <option value="">Select a Campaign...</option>
                      {campaigns
                        .filter(c => c.client_id === parseInt(newLeadForm.client_id))
                        .map(c => <option key={c.name} value={c.name}>{c.name}</option>)
                      }
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Status</label>
                    <select value={newLeadForm.status} onChange={(e) => setNewLeadForm({ ...newLeadForm, status: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]">
                      <option value="new">New</option>
                      <option value="qualified">Qualified</option>
                      <option value="disqualified">Disqualified</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Recording URL / R2 Key</label>
                    <input type="text" value={newLeadForm.recording_url} onChange={(e) => setNewLeadForm({ ...newLeadForm, recording_url: e.target.value })} placeholder="e.g. recordings/John-Doe-1234567890.mp3" className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                </div>

                {/* SECTION: CONTACT INFO */}
                <h4 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4 border-b border-[#e3dbcf] pb-2">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">First Name (Required)</label>
                    <input type="text" value={newLeadForm.owner_firstname} onChange={(e) => setNewLeadForm({ ...newLeadForm, owner_firstname: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Last Name</label>
                    <input type="text" value={newLeadForm.owner_lastname} onChange={(e) => setNewLeadForm({ ...newLeadForm, owner_lastname: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Phone (Required)</label>
                    <input type="text" value={newLeadForm.owner_phone} onChange={(e) => setNewLeadForm({ ...newLeadForm, owner_phone: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Email</label>
                    <input type="email" value={newLeadForm.owner_email} onChange={(e) => setNewLeadForm({ ...newLeadForm, owner_email: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                </div>

                {/* SECTION: PROPERTY DETAILS */}
                <h4 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4 border-b border-[#e3dbcf] pb-2">Property Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:col-span-3">
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Property Address (Required)</label>
                    <input type="text" value={newLeadForm.prop_address} onChange={(e) => setNewLeadForm({ ...newLeadForm, prop_address: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Property Type</label>
                    <select value={newLeadForm.prop_type} onChange={(e) => setNewLeadForm({ ...newLeadForm, prop_type: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]">
                      <option value="SFH">Single Family Home</option>
                      <option value="MFU">Multi-Family Unit</option>
                      <option value="CON">Condo</option>
                      <option value="TWH">Townhouse</option>
                      <option value="VCL">Vacant Land</option>
                      <option value="MOB">Mobile Home</option>
                      <option value="COM">Commercial</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Bedrooms</label>
                    <input type="number" step="0.5" value={newLeadForm.bedrooms} onChange={(e) => setNewLeadForm({ ...newLeadForm, bedrooms: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Bathrooms</label>
                    <input type="number" step="0.5" value={newLeadForm.bathrooms} onChange={(e) => setNewLeadForm({ ...newLeadForm, bathrooms: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Land Size (SqFt)</label>
                    <input type="number" value={newLeadForm.land_size} onChange={(e) => setNewLeadForm({ ...newLeadForm, land_size: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Utilities (0=No, 1=Yes)</label>
                    <select value={newLeadForm.land_utilities} onChange={(e) => setNewLeadForm({ ...newLeadForm, land_utilities: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]">
                      <option value="">Unknown</option>
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Condition Scale (1-10)</label>
                    <input type="number" min="1" max="10" value={newLeadForm.cond_scale} onChange={(e) => setNewLeadForm({ ...newLeadForm, cond_scale: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Updates Needed / Condition Detail</label>
                    <textarea value={newLeadForm.updates_text} onChange={(e) => setNewLeadForm({ ...newLeadForm, updates_text: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a] h-20" />
                  </div>
                </div>

                {/* SECTION: FINANCIALS */}
                <h4 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4 border-b border-[#e3dbcf] pb-2">Financial Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Market Value ($)</label>
                    <input type="number" value={newLeadForm.market_val} onChange={(e) => setNewLeadForm({ ...newLeadForm, market_val: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Asking Price ($)</label>
                    <input type="number" value={newLeadForm.ask_price} onChange={(e) => setNewLeadForm({ ...newLeadForm, ask_price: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Lead Cost ($)</label>
                    <input type="number" value={newLeadForm.lead_cost} onChange={(e) => setNewLeadForm({ ...newLeadForm, lead_cost: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Mortgage Status</label>
                    <select value={newLeadForm.mortgage} onChange={(e) => setNewLeadForm({ ...newLeadForm, mortgage: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]">
                      <option value="0">Free & Clear</option>
                      <option value="1">Has Mortgage</option>
                      <option value="2">Behind/Foreclosure</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Mortgage Amount</label>
                    <input type="number" value={newLeadForm.mortgage_amount} onChange={(e) => setNewLeadForm({ ...newLeadForm, mortgage_amount: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" placeholder={newLeadForm.mortgage !== '1' ? 'N/A' : ''} disabled={newLeadForm.mortgage !== '1' && newLeadForm.mortgage !== '2'} />
                  </div>
                </div>

                {/* SECTION: SELLER SITUATION */}
                <h4 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider mb-4 border-b border-[#e3dbcf] pb-2">Seller Situation</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Motivation (1-10)</label>
                    <input type="number" min="1" max="10" value={newLeadForm.motivation} onChange={(e) => setNewLeadForm({ ...newLeadForm, motivation: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Lead Score (1-10)</label>
                    <input type="number" min="1" max="10" value={newLeadForm.lead_score} onChange={(e) => setNewLeadForm({ ...newLeadForm, lead_score: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">Occupancy</label>
                    <select value={newLeadForm.occupancy} onChange={(e) => setNewLeadForm({ ...newLeadForm, occupancy: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]">
                      <option value="0">Vacant</option>
                      <option value="1">Owner Occupied</option>
                      <option value="2">Tenant Occupied</option>
                      <option value="3">Squatter</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs font-semibold text-[#8c8578] uppercase tracking-widest mb-2 block">General Notes / Info</label>
                    <textarea value={newLeadForm.info} onChange={(e) => setNewLeadForm({ ...newLeadForm, info: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a] h-20" />
                  </div>
                </div>


                <div className="flex gap-4 border-t border-[#e3dbcf] pt-6 mt-6">
                  <button onClick={handleAddLead} disabled={actionLoading} className="flex-1 py-4 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-xl font-medium transition-all text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? 'Creating...' : 'Create Lead'}
                    {actionLoading && <div className="w-5 h-5 border-2 border-[#f3ede3] border-t-transparent rounded-full animate-spin" />}
                  </button>
                  <button onClick={() => setShowAddLead(false)} disabled={actionLoading} className="px-8 py-4 bg-[#fcfbfa] text-[#1a1a1a] border-2 border-[#e3dbcf] hover:bg-[#f3ede3] rounded-xl font-medium transition-all text-lg">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )
        }

      </div>
    </AdminLayout>
  );
}
